import { useCallback, useEffect, useMemo, useState } from "react";
import { networkPillLabel } from "@/lib/networkLabels";
import { Coins, MagnifyingGlass } from "@phosphor-icons/react";
import API from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const PAGE_SIZE = 100;

const NETWORK_COLORS = {
  impact: "bg-blue-50 text-blue-700",
  partnerize: "bg-indigo-50 text-indigo-700",
  skimlinks: "bg-orange-50 text-orange-700",
  mrge: "bg-emerald-50 text-emerald-700",
  cj: "bg-amber-50 text-amber-700",
  pubadhub: "bg-cyan-50 text-cyan-700",
  pubadhub_kevin: "bg-teal-50 text-teal-700",
  admedia: "bg-rose-50 text-rose-700",
  flexoffers: "bg-violet-50 text-violet-700",
  shopnomix_kevin: "bg-fuchsia-50 text-fuchsia-700",
  siteplug: "bg-sky-50 text-sky-700",
  shopnomix: "bg-lime-50 text-lime-700",
  pronomix: "bg-purple-50 text-purple-700",
  maxbounty: "bg-cyan-50 text-cyan-700",
  cashbackhive: "bg-teal-50 text-teal-700",
  fatcoupon: "bg-rose-50 text-rose-700",
  rga: "bg-violet-50 text-violet-700",
  giddyup: "bg-yellow-50 text-yellow-700",
  rga_vishy: "bg-violet-50 text-violet-700",
  rga_ashok: "bg-pink-50 text-pink-700",
};

function NetworkPill({ network }) {
  const n = (network || "").toLowerCase();
  if (!n) return <span className="text-slate-400 text-xs">—</span>;
  const cls = NETWORK_COLORS[n] || "bg-slate-100 text-slate-700";
  const lbl = networkPillLabel(n);
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${cls}`}>
      {lbl}
    </span>
  );
}

function Tag({ tone, children, title }) {
  const cls = {
    best: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pause: "bg-amber-50 text-amber-700 border-amber-200",
    muted: "bg-slate-50 text-slate-500 border-slate-200",
    paused: "bg-rose-50 text-rose-700 border-rose-200",
  }[tone] || "bg-slate-50 text-slate-600 border-slate-200";
  return (
    <span
      title={title}
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${cls}`}
    >
      {children}
    </span>
  );
}

function pct(v) {
  const n = Number(v || 0);
  return `${Number.isInteger(n) ? n : n.toFixed(2).replace(/0+$/, "").replace(/\.$/, "")}%`;
}

function Countries({ list }) {
  if (!list || !list.length) {
    return <span className="text-slate-400" title="No countries declared — only compared with other no-geo copies">any / unset</span>;
  }
  const shown = list.slice(0, 4).join(", ");
  return (
    <span className="font-mono" title={list.join(", ")}>
      {shown}{list.length > 4 ? ` +${list.length - 4}` : ""}
    </span>
  );
}

function errText(err, fallback) {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

function CatalogGroup({ group }) {
  return (
    <div className="border border-slate-200 rounded-md bg-white" data-testid="best-payout-catalog-group">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
        <span className="font-semibold text-slate-900 text-sm">{group.domain}</span>
        <span className="text-xs text-slate-400">{group.networks.length} networks</span>
        {group.tie && <Tag tone="muted">tie</Tag>}
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400">
            <th className="px-3 py-1.5">Network</th>
            <th className="px-3 py-1.5">Brand</th>
            <th className="px-3 py-1.5">Countries</th>
            <th className="px-3 py-1.5">Network pays RedClub</th>
            <th className="px-3 py-1.5" />
          </tr>
        </thead>
        <tbody>
          {group.copies.map((c) => (
            <tr key={c.brand_id} className={`border-t border-slate-50 ${c.is_best ? "bg-emerald-50/40" : ""}`}>
              <td className="px-3 py-1.5"><NetworkPill network={c.network} /></td>
              <td className="px-3 py-1.5">
                <div className="text-slate-800">{c.brand_name || "—"}</div>
                <div className="text-[10px] text-slate-400 font-mono">{c.brand_id}</div>
              </td>
              <td className="px-3 py-1.5"><Countries list={c.countries} /></td>
              <td className="px-3 py-1.5 font-mono">{c.rate_label}</td>
              <td className="px-3 py-1.5">
                {c.is_best && <Tag tone="best" title="Best for its countries">best</Tag>}
                {c.rate_value === null && <Tag tone="muted">not compared</Tag>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PublisherGroup({ group, onApply, onReactivate, busy }) {
  const byId = Object.fromEntries(group.copies.map((c) => [c.brand_id, c]));
  return (
    <div className="border border-slate-200 rounded-md bg-white" data-testid="best-payout-pub-group">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
        <span className="font-semibold text-slate-900 text-sm">{group.domain}</span>
        <span className="text-xs text-slate-400">{group.networks.length} networks</span>
        {group.tie && <Tag tone="muted" title="Two or more networks pay the same — nothing suggested">tie</Tag>}
        <div className="ml-auto">
          {group.suggested.length > 0 && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => onApply([group.domain])}>
              Keep best, route {group.suggested.length}
            </Button>
          )}
        </div>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400">
            <th className="px-3 py-1.5">Network</th>
            <th className="px-3 py-1.5">Brand</th>
            <th className="px-3 py-1.5">Countries</th>
            <th className="px-3 py-1.5">Publisher earns</th>
            <th className="px-3 py-1.5">Network rate (info)</th>
            <th className="px-3 py-1.5">Status</th>
            <th className="px-3 py-1.5" />
          </tr>
        </thead>
        <tbody>
          {group.copies.map((c) => (
            <tr key={c.brand_id} className={`border-t border-slate-50 ${c.is_best ? "bg-emerald-50/40" : ""}`}>
              <td className="px-3 py-1.5"><NetworkPill network={c.network} /></td>
              <td className="px-3 py-1.5">
                <div className="text-slate-800">{c.brand_name || "—"}</div>
                <div className="text-[10px] text-slate-400 font-mono">{c.brand_id}</div>
              </td>
              <td className="px-3 py-1.5"><Countries list={c.countries} /></td>
              <td className="px-3 py-1.5">
                <span className="font-mono font-semibold">{pct(c.pub_pct)}</span>
                <span className="text-[10px] text-slate-400 ml-1">
                  {c.payout_mode === "passthrough" || !c.custom_commission_pct
                    ? `(network × ${c.pub_split_pct ?? "default"} split)`
                    : c.split_applies
                      ? `(${pct(c.custom_commission_pct)} × ${c.pub_split_pct ?? "default"} split)`
                      : `(${pct(c.custom_commission_pct)}, capped at network)`}
                </span>
              </td>
              <td className="px-3 py-1.5 font-mono text-slate-500">{c.network_rate_label}</td>
              <td className="px-3 py-1.5">
                {c.status === "paused"
                  ? <Tag tone="paused" title={c.remap_to ? `Routed to ${c.remap_to}` : undefined}>paused · routed</Tag>
                  : <span className="text-slate-500">{c.status}</span>}
              </td>
              <td className="px-3 py-1.5 space-x-1 whitespace-nowrap">
                {c.is_best && <Tag tone="best" title="Best for its countries">best</Tag>}
                {c.suggest_pause && (
                  <Tag tone="pause" title={`Route to ${c.route_to}`}>
                    route → {byId[c.route_to]?.network || c.route_to}
                  </Tag>
                )}
                {c.geo_mismatch && (
                  <Tag tone="paused" title={`Routed to ${c.remap_to}, which doesn't cover ${(c.countries || []).join(", ") || "this copy's countries"}. Re-activate it.`}>
                    wrong country
                  </Tag>
                )}
                {!c.compared && <Tag tone="muted" title={c.not_compared_reason}>not compared</Tag>}
                {c.status === "paused" && c.paused_reason === "brand_traffic_remap" && (
                  <Button size="sm" variant="ghost" className="h-6 text-xs" disabled={busy} onClick={() => onReactivate(c)}>
                    Re-activate
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminBestPayout() {
  const [pubs, setPubs] = useState([]);
  const [pubFilter, setPubFilter] = useState("");
  const [pubId, setPubId] = useState("");

  const [search, setSearch] = useState("");
  const [catalogQuery, setCatalogQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const [catalog, setCatalog] = useState(null);

  const [pubData, setPubData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const [preview, setPreview] = useState(null); // {domains, rows, skipped}
  const [reactivateRow, setReactivateRow] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/admin/publishers");
        if (!cancelled) setPubs(Array.isArray(data) ? data : (data?.items || []));
      } catch {
        if (!cancelled) toast.error("Failed to load publishers");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // The selected publisher always stays in the list: if the filter hid it,
  // the <select> would show a different name while every button still acts
  // on the hidden pubId.
  const filteredPubs = useMemo(() => {
    const q = pubFilter.trim().toLowerCase();
    if (!q) return pubs;
    return pubs.filter((p) => {
      const id = p.id || p._id || "";
      if (id && id === pubId) return true;
      const hay = `${p.email || ""} ${p.sub_id || ""} ${p.company_name || ""} ${id}`.toLowerCase();
      return hay.includes(q);
    });
  }, [pubs, pubFilter, pubId]);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/admin/best-payout/catalog", {
        params: { search: catalogQuery, limit: PAGE_SIZE, offset },
      });
      setCatalog(data);
    } catch (err) {
      toast.error(errText(err, "Failed to load catalog"));
    } finally {
      setLoading(false);
    }
  }, [catalogQuery, offset]);

  const loadPublisher = useCallback(async () => {
    if (!pubId) return;
    setLoading(true);
    try {
      const { data } = await API.get(`/admin/best-payout/${pubId}`);
      setPubData(data);
    } catch (err) {
      toast.error(errText(err, "Failed to load publisher"));
    } finally {
      setLoading(false);
    }
  }, [pubId]);

  useEffect(() => {
    if (pubId) loadPublisher();
    else loadCatalog();
  }, [pubId, loadPublisher, loadCatalog]);

  const visiblePubGroups = useMemo(() => {
    const groups = pubData?.groups || [];
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => g.domain.includes(q)
      || g.copies.some((c) => (c.brand_name || "").toLowerCase().includes(q)));
  }, [pubData, search]);

  const suggestedDomains = useMemo(
    () => visiblePubGroups.filter((g) => g.suggested.length > 0).map((g) => g.domain),
    [visiblePubGroups],
  );

  const startApply = async (domains) => {
    if (!domains.length) return;
    setBusy(true);
    try {
      const { data } = await API.post(`/admin/best-payout/${pubId}/apply`, { domains, dry_run: true });
      if (!data.would_pause?.length) {
        toast.info("Nothing to change for the selected groups");
        return;
      }
      setPreview({ domains, rows: data.would_pause, skipped: data.skipped || [] });
    } catch (err) {
      toast.error(errText(err, "Preview failed"));
    } finally {
      setBusy(false);
    }
  };

  const confirmApply = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const { data } = await API.post(`/admin/best-payout/${pubId}/apply`, {
        domains: preview.domains,
        dry_run: false,
        brand_ids: preview.rows.map((r) => r.from_brand_id),
        targets: Object.fromEntries(preview.rows.map((r) => [r.from_brand_id, r.to_brand_id])),
      });
      const failed = data.failed || [];
      const skipped = data.skipped || [];
      toast.success(`Routed ${data.applied?.length || 0} copies to the best network`);
      if (failed.length) {
        toast.error(`${failed.length} failed: ${failed.map((f) => `${f.from_brand_name || f.from_brand_id} (${f.error})`).join("; ")}`);
      }
      if (skipped.length) toast.warning(`${skipped.length} skipped (changed since preview)`);
      setPreview(null);
      await loadPublisher();
    } catch (err) {
      toast.error(errText(err, "Apply failed"));
    } finally {
      setBusy(false);
    }
  };

  const confirmReactivate = async () => {
    if (!reactivateRow) return;
    setBusy(true);
    try {
      await API.post(`/admin/best-payout/${pubId}/reactivate`, { brand_id: reactivateRow.brand_id });
      toast.success(`${reactivateRow.brand_name || reactivateRow.brand_id} re-activated, routing removed`);
      setReactivateRow(null);
      await loadPublisher();
    } catch (err) {
      toast.error(errText(err, "Re-activate failed"));
    } finally {
      setBusy(false);
    }
  };

  const total = catalog?.total || 0;

  return (
    <div className="p-8" data-testid="admin-best-payout-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mb-1">
          <Coins size={14} />
          Payouts
        </div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Best Payout</h1>
        <p className="text-sm text-slate-500 mt-1">
          The same advertiser on several networks, matched by domain and compared per country — a
          copy is only routed to one that covers all its countries. Without a publisher this shows
          what each network pays RedClub (read-only). Pick a publisher to compare what they earn on
          each copy they are assigned and route the lower-paying copies to the best one.
        </p>
      </div>

      <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Publisher</label>
          <Input
            className="mt-1 h-8 text-xs"
            placeholder="Filter email / sub_id…"
            value={pubFilter}
            onChange={(e) => setPubFilter(e.target.value)}
          />
          <select
            className="mt-1 w-full h-9 text-sm border border-slate-200 rounded-md bg-white"
            value={pubId}
            onChange={(e) => { setPubId(e.target.value); setPubData(null); }}
            data-testid="best-payout-publisher"
          >
            <option value="">All catalog (no publisher)</option>
            {filteredPubs.map((p) => {
              const id = p.id || p._id || "";
              return (
                <option key={id} value={id}>
                  {(p.email || id) + (p.sub_id ? ` · ${p.sub_id}` : "")}
                </option>
              );
            })}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Search domain / brand</label>
          <form
            className="mt-1 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!pubId) { setOffset(0); setCatalogQuery(search.trim()); }
            }}
          >
            <Input
              className="h-9 text-sm"
              placeholder="adidas.com"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="best-payout-search"
            />
            {!pubId && (
              <Button type="submit" size="sm" variant="outline" className="h-9">
                <MagnifyingGlass size={14} />
              </Button>
            )}
          </form>
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500 py-6">Loading…</div>}

      {!loading && !pubId && catalog && (
        <div className="space-y-3">
          <div className="flex items-center text-xs text-slate-500">
            <span>{total.toLocaleString()} domains on 2+ networks</span>
            <div className="ml-auto flex items-center gap-2">
              <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                Prev
              </Button>
              <span>{total ? `${offset + 1}–${Math.min(offset + PAGE_SIZE, total)}` : "0"}</span>
              <Button size="sm" variant="outline" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
                Next
              </Button>
            </div>
          </div>
          {catalog.groups.length === 0 && (
            <div className="text-sm text-slate-500 py-6">No domains found.</div>
          )}
          {catalog.groups.map((g) => <CatalogGroup key={g.domain} group={g} />)}
        </div>
      )}

      {!loading && pubId && pubData && (
        <div className="space-y-3">
          <div className="flex items-center text-xs text-slate-500 gap-3">
            <span>{visiblePubGroups.length} multi-network domains</span>
            <span>
              {visiblePubGroups.reduce((n, g) => n + g.suggested.length, 0)} copies suggested to route
              {search.trim() ? ` (of ${pubData.suggested_total} for this publisher)` : ""}
            </span>
            {pubData.unmatched > 0 && <span>{pubData.unmatched} assignments without a domain (unmatched)</span>}
            <div className="ml-auto">
              <Button size="sm" disabled={busy || suggestedDomains.length === 0} onClick={() => startApply(suggestedDomains)}>
                Keep best for all {suggestedDomains.length} groups
              </Button>
            </div>
          </div>
          {pubData.geo_mismatch_total > 0 && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
              {pubData.geo_mismatch_total} copies are routed to a copy that doesn&apos;t cover their
              country (marked &ldquo;wrong country&rdquo;). Re-activate them — routing is now per country.
            </div>
          )}
          {visiblePubGroups.length === 0 && (
            <div className="text-sm text-slate-500 py-6" data-testid="best-payout-empty">
              This publisher has no advertiser assigned on more than one network.
            </div>
          )}
          {visiblePubGroups.map((g) => (
            <PublisherGroup
              key={g.domain}
              group={g}
              busy={busy}
              onApply={startApply}
              onReactivate={setReactivateRow}
            />
          ))}
        </div>
      )}

      <AlertDialog open={!!preview} onOpenChange={(o) => { if (!o && !busy) setPreview(null); }}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Pause and route {preview?.rows.length || 0} copies?</AlertDialogTitle>
            <AlertDialogDescription>
              Each copy below is paused for this publisher and its traffic is routed to the
              best-paying copy, so old links keep working. The publisher is not notified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-80 overflow-auto text-xs border border-slate-200 rounded">
            <table className="w-full">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400">
                  <th className="px-2 py-1">Domain</th>
                  <th className="px-2 py-1">Countries</th>
                  <th className="px-2 py-1">Pause</th>
                  <th className="px-2 py-1">Route to</th>
                </tr>
              </thead>
              <tbody>
                {(preview?.rows || []).map((r) => (
                  <tr key={r.from_brand_id} className="border-t border-slate-50">
                    <td className="px-2 py-1">{r.domain}</td>
                    <td className="px-2 py-1"><Countries list={r.countries} /></td>
                    <td className="px-2 py-1">
                      <NetworkPill network={r.from_network} /> {r.from_brand_name} · {pct(r.from_pub_pct)}
                    </td>
                    <td className="px-2 py-1">
                      <NetworkPill network={r.to_network} /> {r.to_brand_name} · {pct(r.to_pub_pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview?.skipped?.length > 0 && (
            <div className="text-xs text-slate-500">{preview.skipped.length} group(s) have nothing to change.</div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmApply(); }}
              disabled={busy}
            >
              {busy ? "Applying…" : "Pause and route"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!reactivateRow} onOpenChange={(o) => { if (!o && !busy) setReactivateRow(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Re-activate {reactivateRow?.brand_name || reactivateRow?.brand_id}?</AlertDialogTitle>
            <AlertDialogDescription>
              The copy becomes active again and its routing to the best copy is removed. The
              publisher is not notified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmReactivate(); }}
              disabled={busy}
            >
              {busy ? "Working…" : "Re-activate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
